import { TimeframeType } from '@/shared/types/timeframe';

import { isValidTimeframe } from './timeframe-manipulator';

/**
 * Gets the interval in milliseconds for a given timeframe
 * @param timeframe - The timeframe to get interval for
 * @returns The interval in milliseconds
 * @throws Error if timeframe is not supported
 */
export const getTimeframeInterval = (timeframe: TimeframeType): number => {
    switch (timeframe) {
        case TimeframeType.M1:
            return 60 * 1000; // 1 minute
        case TimeframeType.M5:
            return 5 * 60 * 1000; // 5 minutes
        case TimeframeType.M15:
            return 15 * 60 * 1000; // 15 minutes
        case TimeframeType.M30:
            return 30 * 60 * 1000; // 30 minutes
        case TimeframeType.H1:
            return 60 * 60 * 1000; // 1 hour
        case TimeframeType.H4:
            return 4 * 60 * 60 * 1000; // 4 hours
        case TimeframeType.D1:
            return 24 * 60 * 60 * 1000; // 1 day
        case TimeframeType.W1:
            return 7 * 24 * 60 * 60 * 1000; // 1 week
        case TimeframeType.MN1:
            return 30 * 24 * 60 * 60 * 1000; // 1 month (approximation)
        default:
            throw new Error(`Unsupported timeframe: ${timeframe}`);
    }
};

/**
 * Generates a series of dates from an end timestamp and timeframe
 * @param endTimestamp - The end timestamp in milliseconds
 * @param timeframe - The timeframe in "MX" format where X is the number of minutes
 * @param count - The number of dates to generate
 * @returns An array of dates
 * @throws Error if timeframe is not supported
 */
export const generateTimeSeriesDates = (
    endTimestamp: number,
    timeframe: TimeframeType,
    count: number,
): Date[] => {
    // Validate timeframe
    if (!isValidTimeframe(timeframe)) {
        throw new Error(`Unsupported timeframe: ${timeframe}`);
    }

    // Get interval in milliseconds
    const intervalMs = getTimeframeInterval(timeframe);

    // Generate dates with end date as starting point
    const endDate = new Date(endTimestamp);

    // Use Array.from instead of a for loop
    return Array.from(
        { length: count },
        (_, i) => new Date(endDate.getTime() + intervalMs * (i + 1)),
    );
};
